import { ChangeDetectionStrategy, Component, computed, inject, input, output, signal } from '@angular/core';
import { DialogRef } from '@angular/cdk/dialog';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AW } from '@airwallet/types';
import { locationTypeValues } from '@airwallet/data';
import { AwModalComponent } from '@components/misc/aw-modal/aw-modal.component';
import { AwButtonComponent } from '@components/misc/aw-button/aw-button.component';
import { LocationService } from '@services/location/location.service';
import { HelperService } from '@services/helper/helper.service';
import { ToastService } from '@services/helper/toast.service';

interface LocationTypeOption {
    value: AW.Location.LocationType;
    label: string;
}

interface CategorizationGroup {
    key: string;
    legacyGroup: string | null;
    locations: AW.Location.UncategorizedLocation[];
}

const LEGACY_GROUP_ORDER: string[] = ['STUDENTS', 'FAMILIES', 'ELDERLY', 'MIXED', 'OTHERS'];

/**
 * Lets a customer assign a location type to their uncategorized locations, grouped by their legacy
 * user group, with an apply-to-group shortcut and per-location override. Progress saves incrementally,
 * so the customer can categorize part of their portfolio now and finish the rest later.
 */
@Component({
    selector: 'app-categorize-locations-modal',
    imports: [AwModalComponent, AwButtonComponent, FormsModule, TranslateModule],
    templateUrl: './categorize-locations-modal.component.html',
    styleUrl: './categorize-locations-modal.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class CategorizeLocationsModalComponent {
    readonly locations = input.required<AW.Location.UncategorizedLocation[]>();
    readonly completed = output<void>();

    private readonly dialogRef = inject<DialogRef<void> | null>(DialogRef, { optional: true });

    readonly locationTypeOptions: LocationTypeOption[];

    private readonly assignments = signal<Record<string, AW.Location.LocationType | null>>({});
    private readonly groupSelections = signal<Record<string, AW.Location.LocationType | null>>({});
    private readonly expanded = signal<Set<string>>(new Set<string>());
    readonly submitting = signal(false);

    readonly groups = computed<CategorizationGroup[]>(() => {
        const byGroup = new Map<string, AW.Location.UncategorizedLocation[]>();
        for (const location of this.locations()) {
            const key = location.legacy_group ?? 'NONE';
            const list = byGroup.get(key) ?? [];
            list.push(location);
            byGroup.set(key, list);
        }

        const ordered: CategorizationGroup[] = [];
        for (const legacyGroup of LEGACY_GROUP_ORDER) {
            const list = byGroup.get(legacyGroup);
            if (list) ordered.push({ key: legacyGroup, legacyGroup, locations: list });
        }
        for (const [key, list] of byGroup) {
            if (key !== 'NONE' && !LEGACY_GROUP_ORDER.includes(key)) {
                ordered.push({ key, legacyGroup: key, locations: list });
            }
        }
        const noneList = byGroup.get('NONE');
        if (noneList) ordered.push({ key: 'NONE', legacyGroup: null, locations: noneList });

        return ordered;
    });

    readonly total = computed(() => this.locations().length);
    readonly categorizedCount = computed(() => {
        const assignments = this.assignments();
        return this.locations().reduce((count, location) => (assignments[location.id] ? count + 1 : count), 0);
    });
    readonly hasChanges = computed(() => this.categorizedCount() > 0);

    constructor(
        private locationService: LocationService,
        private helperService: HelperService,
        private toastService: ToastService,
        private translate: TranslateService
    ) {
        this.locationTypeOptions = locationTypeValues.map(value => ({ value, label: this.translate.instant('location.types.' + value) }));
    }

    groupCategorizedCount(group: CategorizationGroup): number {
        const assignments = this.assignments();
        return group.locations.reduce((count, location) => (assignments[location.id] ? count + 1 : count), 0);
    }

    isExpanded(key: string): boolean {
        return this.expanded().has(key);
    }

    toggleGroup(key: string): void {
        this.expanded.update(current => {
            const next = new Set(current);
            if (next.has(key)) {
                next.delete(key);
            } else {
                next.add(key);
            }
            return next;
        });
    }

    assignmentFor(id: string): AW.Location.LocationType | null {
        return this.assignments()[id] ?? null;
    }

    groupSelectionFor(key: string): AW.Location.LocationType | null {
        return this.groupSelections()[key] ?? null;
    }

    setLocationType(id: string, type: AW.Location.LocationType | null): void {
        this.assignments.update(assignments => ({ ...assignments, [id]: type }));
    }

    applyToGroup(group: CategorizationGroup, type: AW.Location.LocationType | null): void {
        this.groupSelections.update(selections => ({ ...selections, [group.key]: type }));
        if (!type) return;
        this.assignments.update(assignments => {
            const next = { ...assignments };
            for (const location of group.locations) next[location.id] = type;
            return next;
        });
    }

    async submit(): Promise<void> {
        if (!this.hasChanges() || this.submitting()) return;
        this.submitting.set(true);

        const assignments = this.assignments();
        const updates: AW.Location.BulkLocationTypeUpdate[] = this.locations()
            .map(location => ({ id: location.id, location_type: assignments[location.id] }))
            .filter((update): update is AW.Location.BulkLocationTypeUpdate => !!update.location_type);

        try {
            await this.locationService.bulkUpdateLocationType(this.helperService.getDashboardOwnerUser().uid, updates);
            this.completed.emit();
            this.toastService.success(this.translate.instant('location.categorize.save_success'));
            this.dialogRef?.close();
        } catch {
            this.toastService.error(this.translate.instant('location.categorize.save_error'));
            this.submitting.set(false);
        }
    }
}
